/**
 * Treatments Page
 * Vista para gestionar el catálogo de plagas, planes de actuación y materiales
 */

import { useEffect, useState } from "react";
import { Icon, PestModal } from "@/components/ui";
import { plaguesService } from "@/services/plaguesService";
import { notifyAction } from "@/utils";
import type { PlagueCategory } from "@/types";
import styles from "./TreatmentsPage.module.css";

interface ActionStep {
  id: string;
  order: number;
  description: string;
}

interface Material {
  id: string;
  name: string;
  type:
    | "Producto Químico"
    | "Herramienta"
    | "Equipo de Protección"
    | "Material General";
  quantity: number;
  required: boolean; // Si es obligatorio llevarlo
}

interface Pest {
  id: string;
  name: string;
  category: "Insectos" | "Roedores" | "Aves" | "Legionela" | "Otros";
  icon?: string;
  actionPlan: ActionStep[]; // Plan de actuación ordenado
  materials: Material[]; // Materiales y productos necesarios
  preventions: string[]; // Medidas preventivas
  is_recurrent?: boolean;
  recurrence_period?: "week" | "month" | "year" | null;
  image?: string;
}

const categoryFromAPI = (cat: string): Pest["category"] => {
  const map: Record<string, Pest["category"]> = {
    bugs: "Insectos",
    rodents: "Roedores",
    birds: "Aves",
    legionella: "Legionela",
    others: "Otros",
  };
  return map[cat] ?? "Otros";
};

const categoryToAPI = (cat: Pest["category"]): PlagueCategory => {
  const map: Record<Pest["category"], PlagueCategory> = {
    Insectos: "bugs",
    Roedores: "rodents",
    Aves: "birds",
    Legionela: "legionella",
    Otros: "others",
  };
  return map[cat] ?? "others";
};

const getCategoryIcon = (category: Pest["category"]): string => {
  const icons = {
    Insectos: "bug",
    Roedores: "activity",
    Aves: "feather",
    Legionela: "alertTriangle",
    Otros: "help-circle",
  };
  return icons[category] || "help-circle";
};

const recurrenceLabel = (value: Pest["recurrence_period"]): string => {
  const labels: Record<"week" | "month" | "year", string> = {
    week: "semana",
    month: "mes",
    year: "año",
  };
  if (!value) return "";
  return labels[value];
};

export function TreatmentsPage() {
  const [pests, setPests] = useState<Pest[]>([]);
  const [selectedPest, setSelectedPest] = useState<Pest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("Todas");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPest, setEditingPest] = useState<Pest | null>(null);

  useEffect(() => {
    const fetchPests = async () => {
      try {
        setIsLoading(true);
        const res = await plaguesService.list({ page_size: 100 });
        const mapped: Pest[] = res.results.map((p) => ({
          id: String(p.id),
          name: p.name,
          category: categoryFromAPI(p.category),
          icon: p.emoji ?? undefined,
          actionPlan: (p.treatments ?? []).map((t, index) => ({
            id: String(t.id),
            order: index + 1,
            description: t.name,
          })),
          materials:
            p.materials_detail && p.materials_detail.length > 0
              ? p.materials_detail.map((materialEntry) => ({
                  id: String(materialEntry.material.id),
                  name: materialEntry.material.name,
                  type: "Material General",
                  quantity: materialEntry.quantity,
                  required: true,
                }))
              : (p.materials ?? []).map((materialEntry) => ({
                  id: String(materialEntry.material),
                  name: `Material #${materialEntry.material}`,
                  type: "Material General",
                  quantity: materialEntry.quantity,
                  required: true,
                })),
          preventions: (p.precautions ?? []).map(
            (precaution) => precaution.name,
          ),
          is_recurrent: p.is_recurrent ?? false,
          recurrence_period: (p.recurrence_period ?? null) as
            | "week"
            | "month"
            | "year"
            | null,
        }));
        setPests(mapped);
        if (mapped.length > 0) setSelectedPest(mapped[0]);
      } catch (err) {
        setError("Error al cargar el catálogo de plagas");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    void fetchPests();
  }, []);

  const categories = [
    "Todas",
    "Insectos",
    "Roedores",
    "Aves",
    "Legionela",
    "Otros",
  ];

  const filteredPests = pests.filter((pest) => {
    const matchesSearch =
      searchQuery === "" ||
      pest.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      categoryFilter === "Todas" || pest.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleNewPest = () => {
    setEditingPest(null);
    setShowEditModal(true);
  };

  const handleEditPest = (pest: Pest) => {
    setEditingPest(pest);
    setShowEditModal(true);
  };

  const handleDeletePest = (pestId: string) => {
    const pest = pests.find((p) => p.id === pestId);
    notifyAction({
      title: "Eliminar plaga",
      description: `Se eliminará \"${pest?.name ?? "esta plaga"}\" del catálogo.`,
      buttonTitle: "Eliminar",
      onClick: () => {
        void (async () => {
          await plaguesService.remove(Number(pestId));
          const newPests = pests.filter((p) => p.id !== pestId);
          setPests(newPests);
          // Si se elimina la plaga seleccionada, seleccionar otra o null
          if (selectedPest?.id === pestId) {
            setSelectedPest(newPests.length > 0 ? newPests[0] : null);
          }
        })();
      },
    });
  };

  const handleSavePest = async (pest: Pest) => {
    try {
      const payload = {
        name: pest.name,
        category: categoryToAPI(pest.category),
        emoji: pest.icon?.trim() || undefined,
        is_recurrent: pest.is_recurrent ?? false,
        recurrence_period: pest.is_recurrent
          ? (pest.recurrence_period ?? null)
          : null,
        materials: pest.materials
          .map((material) => ({
            material: Number(material.id),
            quantity: Number(material.quantity),
          }))
          .filter(
            (material) =>
              Number.isFinite(material.material) &&
              material.material > 0 &&
              Number.isFinite(material.quantity) &&
              material.quantity > 0,
          ),
        treatments: pest.actionPlan
          .map((step) => step.description.trim())
          .filter((description) => description.length > 0)
          .map((name) => ({ name, description: null })),
        precautions: pest.preventions
          .map((prevention) => prevention.trim())
          .filter((prevention) => prevention.length > 0)
          .map((name) => ({ name, description: null })),
      };
      if (editingPest) {
        await plaguesService.update(Number(pest.id), payload);
        setPests(pests.map((p) => (p.id === pest.id ? pest : p)));
        if (selectedPest?.id === pest.id) setSelectedPest(pest);
      } else {
        const created = await plaguesService.create(payload);
        const newPest = {
          ...pest,
          id: String(created.id),
          icon: created.emoji ?? pest.icon,
        };
        setPests([...pests, newPest]);
        setSelectedPest(newPest);
      }
    } catch (err) {
      console.error("Error guardando plaga:", err);
    }
    setShowEditModal(false);
  };

  if (isLoading)
    return (
      <div className={styles.container}>
        <p>Cargando catálogo de plagas...</p>
      </div>
    );
  if (error)
    return (
      <div className={styles.container}>
        <p>{error}</p>
      </div>
    );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Icon name="bug" size={28} />
            <div>
              <h1>Catálogo de Plagas</h1>
              <p>Gestión de plagas, planes de actuación y materiales</p>
            </div>
          </div>
          <button className={styles.newButton} onClick={handleNewPest}>
            <Icon name="plus" size={20} />
            Nueva Plaga
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchBar}>
          <Icon name="search" size={20} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Buscar por nombre o nombre científico..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.categoryFilters}>
          {categories.map((category) => (
            <button
              key={category}
              className={`${styles.categoryButton} ${
                categoryFilter === category ? styles.active : ""
              }`}
              onClick={() => setCategoryFilter(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Pests List */}
        <div className={styles.pestsList}>
          {filteredPests.map((pest) => (
            <div
              key={pest.id}
              className={`${styles.pestCard} ${
                selectedPest?.id === pest.id ? styles.selected : ""
              }`}
              onClick={() => setSelectedPest(pest)}
            >
              <div className={styles.pestCardHeader}>
                {pest.icon ? (
                  <div className={styles.pestIconEmoji}>{pest.icon}</div>
                ) : (
                  <div className={styles.pestIcon}>
                    <Icon name={getCategoryIcon(pest.category)} size={24} />
                  </div>
                )}
                <div className={styles.pestInfo}>
                  <h3>{pest.name}</h3>
                </div>
              </div>
              <div className={styles.pestCardFooter}>
                <span className={styles.categoryBadge}>{pest.category}</span>
                {pest.is_recurrent && (
                  <span className={styles.riskBadge}>
                    Recurrente · {recurrenceLabel(pest.recurrence_period)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pest Details */}
        {selectedPest && (
          <div className={styles.pestDetails}>
            <div className={styles.detailsHeader}>
              <div className={styles.detailsHeaderRow}>
                <div className={styles.detailsTitle}>
                  {selectedPest.icon ? (
                    <div className={styles.detailsIconEmoji}>
                      {selectedPest.icon}
                    </div>
                  ) : (
                    <Icon
                      name={getCategoryIcon(selectedPest.category)}
                      size={32}
                    />
                  )}
                  <div>
                    <h2>{selectedPest.name}</h2>
                    <div className={styles.detailsBadges}>
                      <span className={styles.categoryBadge}>
                        {selectedPest.category}
                      </span>
                      {selectedPest.is_recurrent && (
                        <span className={styles.riskBadge}>
                          Recurrente ·{" "}
                          {recurrenceLabel(selectedPest.recurrence_period)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.detailsActions}>
                  <button
                    className={styles.editButton}
                    onClick={() => handleEditPest(selectedPest)}
                    title="Editar"
                  >
                    <Icon name="edit" size={18} />
                  </button>
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleDeletePest(selectedPest.id)}
                    title="Eliminar"
                  >
                    <Icon name="xCircle" size={18} />
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.detailsContent}>
              {/* Action Plan - NEW */}
              {selectedPest.actionPlan &&
                selectedPest.actionPlan.length > 0 && (
                  <section className={styles.section}>
                    <h3>
                      <Icon name="list" size={20} />
                      Plan de Actuación
                    </h3>
                    <div className={styles.actionPlan}>
                      {selectedPest.actionPlan
                        .sort((a, b) => a.order - b.order)
                        .map((step) => (
                          <div key={step.id} className={styles.actionStep}>
                            <div className={styles.stepNumber}>
                              {step.order}
                            </div>
                            <div className={styles.stepContent}>
                              <p className={styles.stepDescription}>
                                {step.description}
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </section>
                )}

              {/* Materials - NEW */}
              {selectedPest.materials && selectedPest.materials.length > 0 && (
                <section className={styles.section}>
                  <h3>
                    <Icon name="package" size={20} />
                    Materiales Necesarios
                  </h3>
                  <div className={styles.materialsGrid}>
                    {selectedPest.materials.map((material) => (
                      <div
                        key={material.id}
                        className={`${styles.materialCard} ${material.required ? styles.required : ""}`}
                      >
                        <div className={styles.materialHeader}>
                          <span className={styles.materialName}>
                            {material.name}
                          </span>
                          {material.required && (
                            <span className={styles.requiredBadge}>
                              Obligatorio
                            </span>
                          )}
                        </div>
                        <div className={styles.materialDetails}>
                          <span className={styles.materialType}>
                            {material.type}
                          </span>
                          <span className={styles.materialQuantity}>
                            {material.quantity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Preventions */}
              <section className={styles.section}>
                <h3>
                  <Icon name="checkCircle" size={20} />
                  Medidas Preventivas
                </h3>
                <ul>
                  {selectedPest.preventions.map((prevention, index) => (
                    <li key={index}>{prevention}</li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        )}
      </div>

      {/* Pest Edit/Create Modal */}
      <PestModal
        isOpen={showEditModal}
        pest={editingPest}
        onClose={() => setShowEditModal(false)}
        // @ts-ignore - Pest type id optionality conflict
        onSave={handleSavePest}
      />
    </div>
  );
}
